import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

/**
 * Combines class names using clsx and tailwind-merge
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Formats a date for display
 */
export function formatDate(date: Date | { toDate: () => Date }): string {
  const d = "toDate" in date ? date.toDate() : date;
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/**
 * Formats a relative time (e.g., "2 days ago")
 */
export function formatRelativeTime(date: Date | { toDate: () => Date }): string {
  const d = "toDate" in date ? date.toDate() : date;
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    if (diffHours === 0) {
      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      return diffMinutes <= 1 ? "Just now" : `${diffMinutes} minutes ago`;
    }
    return diffHours === 1 ? "1 hour ago" : `${diffHours} hours ago`;
  }

  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
  return `${Math.floor(diffDays / 365)} years ago`;
}

/**
 * Calculates word strength based on review stats
 */
export function calculateStrength(
  knownCount: number,
  unknownCount: number
): "weak" | "learning" | "strong" {
  const total = knownCount + unknownCount;
  if (total === 0) return "weak"; // Never reviewed

  const successRate = knownCount / total;

  if (successRate >= 0.8 && total >= 3) return "strong";
  if (successRate >= 0.5) return "learning";
  return "weak";
}

/**
 * Gets the appropriate CSS class for a strength level
 */
export function getStrengthClass(strength: "weak" | "learning" | "strong"): string {
  switch (strength) {
    case "strong":
      return "strength-strong";
    case "learning":
      return "strength-learning";
    case "weak":
    default:
      return "strength-weak";
  }
}

/**
 * Truncates text with ellipsis
 */
export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 3) + "...";
}
